import {faFileImage} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import debounce from 'lodash/debounce';
import isArray from 'lodash/isArray';
import PropTypes from 'prop-types';
import React, {useEffect, useReducer, useRef, useState} from 'react';
import './styles.css';
import Avatar from '../../components/Avatar';
import BasicButton from '../../components/BasicButton';
import BasicTextArea from '../../components/BasicTextArea';
import DragAndDrop from '../../components/DragAndDrop';
import LoadMask from '../../components/LoadMask';
import MinimalSelect from '../../components/MinimalSelect';
import RemoveButton from '../../components/RemoveButton';
import ENDPOINT from '../../constants/endpoints';
import FileHelper from '../../helpers/file';
import LinkBuilder from '../../helpers/link-builder';
import LinkParser from '../../helpers/link-parser';
import {Role} from '../../helpers/user';
import useApi from '../../hooks/useApi';
import ResponseErrorDialog from '../Dialog/components/ResponseErrorDialog';
import FeedPostFormPreview from './components/FeedPostFormPreview';
import {getBase64} from './helper';
import {FEED_POST_FORM_ACTION, FeedPostFormReducer} from './reducer';

const initialState = {
  share: { value: 'Public', label: 'Public' },
  text: '',
  parsedData: null,
  files: [],
  isLoading: false,
  isParseModeOn: true,
  parseLink: false,
  showImage: false,
  showShareLinkPreview: false,
  attach: null,
  attachPreview: null,
  attachError: null,
};

const FeedPostForm = props => {
  const {
    user,
    user: { name, userLink, avatar },
    onSubmitFormHandler,
  } = props;

  const fileInputRef = useRef(null);

  const api = useApi();

  const [shareLink, setShareLink] = useState();
  const [state, dispatch] = useReducer(FeedPostFormReducer, initialState);

  useEffect(() => {
    if (shareLink) {
      dispatch({ type: FEED_POST_FORM_ACTION.PARSE_REQUEST });

      api.get(
        ENDPOINT.ACTION.FEED.PARSE_SHARE_LINK,
        { url: shareLink },
        {
          handleError: response => {
            dispatch({
              type: FEED_POST_FORM_ACTION.PARSE_SUCCESS,
              data: response,
            });
          },
          handleSuccess: response => {
            dispatch({
              type: FEED_POST_FORM_ACTION.PARSE_SUCCESS,
              data: response,
            });
          },
        }
      );
    }
  }, [shareLink]);

  const validatePostMoodImage = async file => {
    const maxSize = 2; // MB

    let error = null;
    if (!new RegExp(/\.(gif|jpe?g|png)$/i).test(file.name)) {
      error = 'This type of file is not supported';
    }

    if (!error && file.size > maxSize * 1024 * 1024) {
      error = `The file is too big. Max file size is ${maxSize}MB`;
    }

    if (!error) {
      const size = await FileHelper.getImageSize(file);
      if (size.width <= 80 || size.width <= 40) {
        error = `Please select an image bigger than 80 x 40`;
      }
    }

    return error;
  };

  const handleFormSubmit = () => {
    const { share, text, parsedData, attach } = state;

    let data = {
      Share: share.value,
      Text: text,
      Image: attach,
      url: shareLink || '',
      title: '',
      titleEdited: '',
      content: '',
      contentEdited: '',
      currentImageIdx: 0,
      openBlock: 'N',
      images: [],
    };

    if (parsedData) {
      data = { ...data, ...parsedData, openBlock: 'Y' };
      if (parsedData.currentImageIdx === -1) {
        delete data.images;
        data.currentImageIdx = 0;
      }
    }

    if (
      !Role.isCandidate(user, true) ||
      !(data.Image || data.Text || data.url)
    ) {
      return;
    }

    dispatch({ type: FEED_POST_FORM_ACTION.SHOW_LOADER });

    api.post(
      ENDPOINT.ACTION.FEED.POST_MOOD,
      { ...data },
      {
        handleError: response => {
          const { data } = response;
          let message = null;
          if (data.errors && data.errors.Text && isArray(data.errors.Text)) {
            const [error] = data.errors.Text;
            if (error === 'Field is required') {
              message = 'Please enter your message';
            } else if (error === 'String is too long.') {
              message = 'Please input a message less than 500 characters.';
            }
          } else if (
            data.errors &&
            data.errors.Image &&
            isArray(data.errors.Image)
          ) {
            const [error] = data.errors.Image;
            if (error === 'image80x40') {
              message = 'Please select an image bigger than 80x40px';
            }
          }
          ResponseErrorDialog({ response: message ? { message } : response });
        },
        handleSuccess: () => {
          dispatch({
            type: FEED_POST_FORM_ACTION.SET_SATE,
            data: initialState,
          });
          setShareLink();
          onSubmitFormHandler();
        },
        handleComplete: () => [
          dispatch({
            type: FEED_POST_FORM_ACTION.HIDE_LOADER,
          }),
        ],
      }
    );
  };

  const attachImage = async files => {
    removeAttach();

    const [file = null] = files;
    let error = await validatePostMoodImage(file);

    if (!error) {
      getBase64(file).then(image => {
        dispatch({
          type: FEED_POST_FORM_ACTION.ATTACH_IMAGE,
          data: { attach: file, attachPreview: image },
        });
      });
    } else {
      dispatch({ type: FEED_POST_FORM_ACTION.ATTACH_IMAGE_FAIL, data: error });
    }
  };

  const handleDrop = async files => {
    await attachImage(files);
  };

  const handleImageUpload = async e => {
    const { files = [] } = e.target;
    await attachImage(files);
    fileInputRef.current.value = '';
  };

  const handleRemoveParsedData = () => {
    dispatch({ type: FEED_POST_FORM_ACTION.REMOVE_PARSED_DATA });
  };

  const removeAttach = () => {
    dispatch({ type: FEED_POST_FORM_ACTION.REMOVE_ATTACHED_IMAGE });
  };

  const parseFormText = debounce(text => {
    if (state.isParseModeOn) {
      const urls = LinkParser.findUrls(text);
      if (urls !== null && urls.length > 0) {
        setShareLink(urls[0]);
      }
    }
  }, 300);

  const handleKeyUpFormText = (name, e) => {
    const {
      target: { value },
    } = e;
    parseFormText(value);
  };

  const handleChangeFormText = e => {
    const {
      target: { value },
    } = e;
    dispatch({
      type: FEED_POST_FORM_ACTION.CHANGE_TEXT,
      data: value.length > 500 ? value.substring(0, 500) : value,
    });
  };

  const handleSharingOptions = option => {
    dispatch({
      type: FEED_POST_FORM_ACTION.CHANGE_SELECTBOX,
      data: option,
    });
  };

  const handleChangePreviewImage = imageIndex => {
    dispatch({
      type: FEED_POST_FORM_ACTION.CHANGE_PARSED_DATA,
      data: {
        currentImageIdx: imageIndex,
      },
    });
  };

  const handleChangeTitle = text => {
    dispatch({
      type: FEED_POST_FORM_ACTION.CHANGE_PARSED_DATA,
      data: {
        titleEdited: text.length > 70 ? text.substring(0, 70) : text,
      },
    });
  };

  const handleChangeText = text => {
    dispatch({
      type: FEED_POST_FORM_ACTION.CHANGE_PARSED_DATA,
      data: {
        contentEdited: text.length > 250 ? text.substring(0, 250) : text,
      },
    });
  };

  return (
    <article className="new-feed-post">
      <div className="new-feed-post__avatar">
        <Avatar
          size={55}
          link={userLink}
          name={name}
          image={LinkBuilder.getCandidatePictureSizeLink(avatar, '65x65')}
        />
      </div>

      <div className="new-feed-post__container">
        <div className="new-feed-post__content">
          {state.isLoading && <LoadMask relative />}

          <form
            className="new-feed-post__form-container"
            onSubmit={handleFormSubmit}
          >
            <DragAndDrop handleDrop={handleDrop}>
              {!state.showShareLinkPreview && (
                <label className="new-feed-post__upload">
                  <FontAwesomeIcon
                    icon={faFileImage}
                    color="#a0a0a0"
                    size="lg"
                    className="new-feed-post__upload-icon"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="new-feed-post__upload-input"
                    onChange={handleImageUpload}
                  />
                </label>
              )}

              <BasicTextArea
                rows={3}
                disabled={state.isLoading ? true : null}
                name="post content"
                content={state.text}
                resize={false}
                placeholder="Share your updates"
                controlFunc={handleChangeFormText}
                onKeyUp={handleKeyUpFormText}
                isValid={true}
              />
            </DragAndDrop>

            {state.showShareLinkPreview && (
              <div className="new-feed-post__preview">
                <FeedPostFormPreview
                  data={state.parsedData}
                  onChangeImage={handleChangePreviewImage}
                  onChangeTitle={handleChangeTitle}
                  onChangeText={handleChangeText}
                  onRemove={handleRemoveParsedData}
                />
              </div>
            )}
            {state.attachError && (
              <p className="new-feed-post__error-msg">{state.attachError}</p>
            )}
            {state.attachPreview && (
              <div className="new-feed-post__preview">
                <RemoveButton
                  controlFunc={removeAttach}
                  customStyles={{
                    top: '10px',
                    right: '0',
                  }}
                />
                <p className="new-feed-post__preview-text">{state.text}</p>
                <div className="new-feed-post__thumbnail">
                  <img
                    className="new-feed-post__thumbnail-image"
                    src={state.attachPreview}
                    alt="Preview"
                  />
                </div>
              </div>
            )}
            <div className="new-feed-post__controls">
              <div className="new-feed-post__sharing-options">
                <label className="new-feed-post__label">Share with:</label>
                <MinimalSelect
                  name="Share"
                  value={state.share}
                  controlFunc={handleSharingOptions}
                  options={[
                    { value: 'Public', label: 'Public' },
                    { value: 'Connected', label: 'Connections' },
                  ]}
                />
              </div>

              <div className="new-feed-post__submit-btn">
                <BasicButton
                  btnWidth={120}
                  btnHeight={35}
                  btnTextColor="#27282d"
                  btnFontWeight={600}
                  btnFontSize="0.8571rem"
                  btnText="Post"
                  btnArrow={false}
                  controlFunc={handleFormSubmit}
                  disabled={state.isLoading}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </article>
  );
};

FeedPostForm.propTypes = {
  user: PropTypes.object.isRequired,
  onSubmitFormHandler: PropTypes.func.isRequired,
};

export default FeedPostForm;
